import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseRecordingProps {
  meetingId: string;
  stream: MediaStream | null;
}

export function useRecording({ meetingId, stream }: UseRecordingProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const durationIntervalRef = useRef<number | null>(null);
  const mimeTypeRef = useRef<string>('video/webm');
  const { toast } = useToast();

  const getSupportedMimeType = useCallback(() => {
    if (typeof MediaRecorder === "undefined") return null;

    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/mp4;codecs=h264,aac',
      'video/webm'
    ];

    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || null;
  }, []);

  const startRecording = useCallback(async () => {
    if (!stream) {
      toast({
        title: "Cannot start recording",
        description: "No media stream available",
        variant: "destructive",
      });
      return;
    }

    try {
      const supportedMimeType = getSupportedMimeType();

      if (!supportedMimeType) {
        toast({
          title: "Recording unavailable",
          description: "This browser doesn't support the required recording format.",
          variant: "destructive",
        });
        return;
      }

      // Create media recorder with the stream
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: supportedMimeType,
      });
      mimeTypeRef.current = supportedMimeType;

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        await saveRecording();
      };

      mediaRecorder.start(1000); // Collect data every second
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);

      // Update database
      await supabase
        .from('meetings')
        .update({ is_recording: true })
        .eq('id', meetingId);

      // Start duration counter
      setRecordingDuration(0);
      durationIntervalRef.current = window.setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      toast({
        title: "Recording started",
        description: "The meeting is now being recorded",
      });
    } catch (error: any) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording error",
        description: error.message,
        variant: "destructive",
      });
    }
  }, [stream, meetingId, toast, getSupportedMimeType]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }
  }, [isRecording]);

  const saveRecording = async () => {
    try {
      if (!chunksRef.current.length) {
        toast({
          title: "No recording data",
          description: "We couldn't find any recorded segments to upload.",
          variant: "destructive",
        });
        return;
      }

      const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
      const fileName = `recording-${Date.now()}.webm`;
      const filePath = `${meetingId}/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('meeting-recordings')
        .upload(filePath, blob, {
          contentType: 'video/webm',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // Save recording metadata to database
      const { error: dbError } = await supabase
        .from('recordings')
        .insert({
          meeting_id: meetingId,
          file_name: fileName,
          file_path: filePath,
          file_size: blob.size,
          duration: recordingDuration,
          format: 'webm',
          status: 'completed',
          created_by: user?.id
        });

      if (dbError) {
        console.error('Error saving recording metadata:', dbError);
        // Continue anyway - file is uploaded
      }

      // Update meeting status
      await supabase
        .from('meetings')
        .update({ is_recording: false })
        .eq('id', meetingId);

      toast({
        title: "Recording saved",
        description: "The recording has been saved successfully",
      });

      // Reset
      chunksRef.current = [];
      setRecordingDuration(0);
    } catch (error: any) {
      console.error('Error saving recording:', error);
      
      // Update database even if upload fails
      await supabase
        .from('meetings')
        .update({ is_recording: false })
        .eq('id', meetingId);

      toast({
        title: "Error saving recording",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return {
    isRecording,
    recordingDuration: formatDuration(recordingDuration),
    startRecording,
    stopRecording,
  };
}
